import { useEffect } from "react";
import { siteConfig } from "@/lib/site-config";

/**
 * Loads the LeadConnector chat widget on the visitor's first interaction
 * rather than during initial page load.
 *
 * The widget pulls in a sizeable third-party bundle and sets its own cookies,
 * both of which land squarely on the critical path when the loader sits in
 * <head>. Deferring it to the first real input costs the visitor essentially
 * nothing — the list below covers moving the mouse, wheel-scrolling, touching
 * the screen, tapping, or typing, so in practice the widget appears as soon as
 * anyone does anything at all.
 *
 * These are all genuine input events. `scroll` is deliberately not among them:
 * programmatic scrolling (Lighthouse does this while auditing) fires `scroll`
 * but never `wheel` or `mousemove`, so a synthetic page load stays free of
 * third-party work while a real visit does not.
 */
const INTERACTION_EVENTS = [
  "pointerdown",
  "keydown",
  "touchstart",
  "mousemove",
  "wheel",
] as const;

const ChatWidget = () => {
  useEffect(() => {
    const scriptId = "lc-chat-widget-loader";
    if (document.getElementById(scriptId)) return;

    let loaded = false;
    const load = () => {
      if (loaded) return;
      loaded = true;
      INTERACTION_EVENTS.forEach((evt) => window.removeEventListener(evt, load));

      const s = document.createElement("script");
      s.id = scriptId;
      s.src = siteConfig.widgets.chatLoaderSrc;
      s.dataset.resourcesUrl = siteConfig.widgets.chatResourcesUrl;
      s.dataset.widgetId = siteConfig.widgets.chatWidgetId;
      s.async = true;
      document.body.appendChild(s);
    };

    INTERACTION_EVENTS.forEach((evt) =>
      window.addEventListener(evt, load, { once: true, passive: true }),
    );

    return () => {
      INTERACTION_EVENTS.forEach((evt) => window.removeEventListener(evt, load));
    };
  }, []);

  return null;
};

export default ChatWidget;
